import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { isSubscriptionBlockedError } from "../../utils/subscriptionError";
import { updateTaskTemplateFieldOptions } from "../../redux/Slice/Tasks/taskTemplatesSlice";
import {
  DEFAULT_CUSTOM_OPTION_COLOR,
  DEFAULT_PRIORITY_OPTIONS,
  DEFAULT_TYPE_OPTIONS,
  getFieldOptionChipSx,
  isLegacyOverdueStatusValue,
  mergeStatusOptionsWithDefaults,
  normalizeFieldOptions,
  normalizeHexColor,
} from "../../utils/taskTemplateChipStyles";

const TASK_FIELD_DARK_BG = "#121212";
const TASK_FIELD_DARK_SURFACE = "#1E1E1E";
const TASK_FIELD_DARK_INPUT = "#1E1E1E";
const TASK_FIELD_DARK_TEXT = "#f1f5f9";
const TASK_FIELD_DARK_MUTED = "rgba(226, 232, 240, 0.78)";
const TASK_FIELD_DARK_BORDER = "rgba(148, 163, 184, 0.45)";

const fieldOptionsDialogSx = {
  bgcolor: (theme) => (theme.palette.mode === "dark" ? TASK_FIELD_DARK_BG : undefined),
  color: (theme) => (theme.palette.mode === "dark" ? TASK_FIELD_DARK_TEXT : undefined),
  "& .MuiDialogTitle-root, & .MuiDialogContent-root, & .MuiDialogActions-root": {
    bgcolor: (theme) => (theme.palette.mode === "dark" ? TASK_FIELD_DARK_BG : undefined),
    color: (theme) => (theme.palette.mode === "dark" ? TASK_FIELD_DARK_TEXT : undefined),
  },
  "& .MuiTypography-root": {
    color: (theme) => (theme.palette.mode === "dark" ? TASK_FIELD_DARK_TEXT : undefined),
  },
  "& .MuiTypography-colorTextSecondary": {
    color: (theme) => (theme.palette.mode === "dark" ? TASK_FIELD_DARK_MUTED : undefined),
  },
  "& .MuiInputBase-root": {
    color: (theme) => (theme.palette.mode === "dark" ? TASK_FIELD_DARK_TEXT : undefined),
    bgcolor: (theme) => (theme.palette.mode === "dark" ? TASK_FIELD_DARK_INPUT : undefined),
  },
  "& .MuiInputLabel-root": {
    color: (theme) => (theme.palette.mode === "dark" ? TASK_FIELD_DARK_MUTED : undefined),
    fontSize: "0.9rem",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: (theme) => (theme.palette.mode === "dark" ? TASK_FIELD_DARK_BORDER : undefined),
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: (theme) => (theme.palette.mode === "dark" ? TASK_FIELD_DARK_MUTED : undefined),
  },
  // Comfortable, consistent typography (theme base is an intentionally tiny 12px).
  "& .MuiInputBase-input": { fontSize: "0.9rem" },
  "& .MuiButton-root": { fontSize: "0.875rem", fontWeight: 600 },
  "& .MuiChip-label": { fontSize: "0.8rem" },
};

const fieldOptionsTitleSx = {
  fontWeight: 700,
  fontSize: { xs: "1rem", sm: "1.05rem" },
  letterSpacing: "-0.01em",
  color: (theme) => (theme.palette.mode === "dark" ? TASK_FIELD_DARK_TEXT : "#1f2933"),
};

const fieldOptionsHelperSx = {
  fontSize: { xs: "0.82rem", sm: "0.85rem" },
  lineHeight: 1.55,
};

const TaskTemplateFieldOptionsDialog = ({ open, onClose, kind = "status" }) => {
  const dispatch = useDispatch();
  const saving = useSelector((s) => s.taskTemplates.fieldOptionsSaving);
  const stored = useSelector((s) => s.taskTemplates.fieldOptions);
  const [items, setItems] = useState([]);
  const [newValue, setNewValue] = useState("");
  const [newColor, setNewColor] = useState(DEFAULT_CUSTOM_OPTION_COLOR);
  const [newColorChosen, setNewColorChosen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editColor, setEditColor] = useState(DEFAULT_CUSTOM_OPTION_COLOR);

  useEffect(() => {
    if (!open) return;
    const source =
      kind === "type"
        ? stored.typeOptions?.length
          ? stored.typeOptions
          : DEFAULT_TYPE_OPTIONS
        : kind === "status"
          ? mergeStatusOptionsWithDefaults(stored.statusOptions || [])
          : stored.priorityOptions?.length
            ? stored.priorityOptions
            : DEFAULT_PRIORITY_OPTIONS;
    setItems(normalizeFieldOptions(source, kind));
    setNewValue("");
    setNewColor(DEFAULT_CUSTOM_OPTION_COLOR);
    setNewColorChosen(false);
    setEditingIndex(null);
    setEditValue("");
    setEditColor(DEFAULT_CUSTOM_OPTION_COLOR);
  }, [open, kind, stored.typeOptions, stored.statusOptions, stored.priorityOptions]);

  const title =
    kind === "type"
      ? "Manage type values"
      : kind === "status"
        ? "Manage status values"
        : "Manage priority values";

  const rowBoxSx = (theme) => ({
    borderRadius: 1,
    px: 1,
    py: 0.75,
    bgcolor: theme.palette.mode === "dark" ? TASK_FIELD_DARK_SURFACE : theme.palette.action.hover,
    border: theme.palette.mode === "dark" ? `1px solid ${TASK_FIELD_DARK_BORDER}` : undefined,
  });

  const handleAdd = () => {
    const v = newValue.trim();
    if (!v) return;
    if (kind === "status" && isLegacyOverdueStatusValue(v)) {
      toast.error("Over Due is no longer a status option.");
      return;
    }
    if (items.some((x) => x.value.toLowerCase() === v.toLowerCase())) {
      toast.error("That value already exists.");
      return;
    }
    if (!newColorChosen) {
      toast.error("Please choose a color first.");
      return;
    }
    setItems((prev) => [...prev, { value: v, color: normalizeHexColor(newColor) }]);
    setNewValue("");
    setNewColor(DEFAULT_CUSTOM_OPTION_COLOR);
    setNewColorChosen(false);
  };

  const handleDelete = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
    if (editingIndex === index) {
      setEditingIndex(null);
      setEditValue("");
      setEditColor(DEFAULT_CUSTOM_OPTION_COLOR);
    }
  };

  const startEdit = (index, value) => {
    setEditingIndex(index);
    setEditValue(value.value);
    setEditColor(normalizeHexColor(value.color));
  };

  const commitEdit = () => {
    if (editingIndex === null) return;
    const v = editValue.trim();
    if (!v) {
      toast.error("Value cannot be empty.");
      return;
    }
    if (kind === "status" && isLegacyOverdueStatusValue(v)) {
      toast.error("Over Due is no longer a status option.");
      return;
    }
    const other = items.filter((_, i) => i !== editingIndex);
    if (other.some((x) => x.value.toLowerCase() === v.toLowerCase())) {
      toast.error("Another entry already uses this value.");
      return;
    }
    setItems((prev) =>
      prev.map((x, i) =>
        i === editingIndex ? { value: v, color: normalizeHexColor(editColor) } : x
      )
    );
    setEditingIndex(null);
    setEditValue("");
    setEditColor(DEFAULT_CUSTOM_OPTION_COLOR);
  };

  const payloadKey =
    kind === "type" ? "typeOptions" : kind === "status" ? "statusOptions" : "priorityOptions";

  const handleSave = async () => {
    try {
      const payloadItems =
        kind === "status"
          ? normalizeFieldOptions(items, "status").filter(
              (item) => !isLegacyOverdueStatusValue(item.value)
            )
          : items;
      await dispatch(
        updateTaskTemplateFieldOptions({
          [payloadKey]: payloadItems,
        })
      ).unwrap();
      toast.success("Options saved.");
      onClose();
    } catch (e) {
      if (!isSubscriptionBlockedError(e)) {
        toast.error(e || "Failed to save options.");
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: fieldOptionsDialogSx }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={fieldOptionsTitleSx}>{title}</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary" sx={fieldOptionsHelperSx}>
            Saved values power the form dropdown and the colored chips shown for this field across templates.
          </Typography>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              size="small"
              fullWidth
              label="New value"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAdd();
                }
              }}
            />
            <TextField
              size="small"
              label="Color"
              type="color"
              value={newColor}
              onChange={(e) => {
                setNewColor(normalizeHexColor(e.target.value));
                setNewColorChosen(true);
              }}
              sx={{ width: 92 }}
              InputLabelProps={{ shrink: true }}
            />
            <Button variant="outlined" onClick={handleAdd} sx={{ mt: 0.5, textTransform: "none" }}>
              Add
            </Button>
          </Stack>
          <List dense disablePadding>
            {items.map((v, i) => (
              <ListItem
                key={`${v.value}_${i}`}
                disableGutters
                sx={{ display: "block", mb: 1 }}
              >
                <Box sx={rowBoxSx}>
                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                    <Chip
                      size="small"
                      label={v.value}
                      sx={getFieldOptionChipSx(v.value, items, kind)}
                    />
                    {editingIndex === i ? (
                      <>
                        <TextField
                          size="small"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          sx={{ flex: 1, minWidth: 140 }}
                        />
                        <TextField
                          size="small"
                          type="color"
                          label="Color"
                          value={editColor}
                          onChange={(e) => setEditColor(normalizeHexColor(e.target.value))}
                          sx={{ width: 92 }}
                          InputLabelProps={{ shrink: true }}
                        />
                        <IconButton size="small" color="primary" onClick={commitEdit}>
                          <CheckIcon fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <Box sx={{ flex: 1 }} />
                        <IconButton size="small" onClick={() => startEdit(i, v)} aria-label="Edit">
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(i)} aria-label="Delete">
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </Stack>
                </Box>
              </ListItem>
            ))}
          </List>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ textTransform: "none" }}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskTemplateFieldOptionsDialog;
